import _jsx from "@babel/runtime/helpers/jsx";
import PropTypes from 'prop-types';
import React from 'react';
import { deprecate } from 'react-is-deprecated';
import { createChainedFunction } from '@atc/create-chained-function';
import clsx from 'clsx';
import { ItemCard, Panel, Checkbox, Text, Image } from 'reaxl';
import ActiveDealIndicator from '../ActiveDealIndicator/index.js';
import InventoryListingBodyV2 from '../InventoryListingBodyV2/index.js';
import InventoryListingFooter from '../InventoryListingFooter/index.js';
import InventoryListingAddOns from '../InventoryListingAddOns/index.js';
import InventoryListingSideBar from '../InventoryListingSideBar/index.js';
import InventoryListingOverlayActions from '../InventoryListingOverlayActions/index.js';
const renderSideBar = _ref => {
  let {
    listing,
    uiContext,
    ...rest
  } = _ref;
  return /*#__PURE__*/React.createElement(InventoryListingSideBar, Object.assign({
    data: listing,
    uiContext: uiContext
  }, rest));
};
const renderBody = _ref2 => {
  let {
    isActiveDeal,
    uiContext,
    ...rest
  } = _ref2;
  return /*#__PURE__*/React.createElement(InventoryListingBodyV2, Object.assign({
    uiContext: uiContext,
    isActiveDeal: isActiveDeal
  }, rest));
};
const renderUrgencyDriver = _ref3 => {
  let {
    text,
    subText,
    color,
    imgSrc
  } = _ref3;
  return /*#__PURE__*/_jsx("div", {
    className: "display-flex align-items-center margin-left-2 row"
  }, void 0, /*#__PURE__*/_jsx("div", {
    className: "col-xs-9"
  }, void 0, /*#__PURE__*/_jsx(Text, {
    className: "display-inline-block align-self-end",
    color: color,
    weight: "bold",
    size: 100
  }, void 0, text), /*#__PURE__*/_jsx(Text, {
    className: "display-inline-block align-self-end",
    color: color,
    weight: "bold",
    size: 100
  }, void 0, subText)), imgSrc && /*#__PURE__*/_jsx("div", {
    className: "col-xs-3"
  }, void 0, /*#__PURE__*/_jsx(Image, {
    height: 24,
    width: 24,
    src: imgSrc,
    alt: ""
  })));
};
const renderFooter = _ref4 => {
  let {
    showFooter,
    isActiveDeal,
    onActiveDealDetailsClick,
    onCheckStatusClick,
    orientation,
    listing,
    dealsDuck,
    paymentsDuck,
    interestRateDuck,
    saveDealXgFetcher,
    ...props
  } = _ref4;
  if (listing?.urgencyDriver && Object.keys(listing.urgencyDriver).length !== 0) {
    return renderUrgencyDriver(listing.urgencyDriver);
  }
  return listing.owner && showFooter && (isActiveDeal ? /*#__PURE__*/_jsx(ActiveDealIndicator, {
    orientation: orientation,
    onActiveDealDetailsClick: onActiveDealDetailsClick,
    onCheckStatusClick: onCheckStatusClick
  }) : /*#__PURE__*/React.createElement(InventoryListingFooter, Object.assign({
    orientation: orientation,
    listing: listing,
    dealsDuck: dealsDuck,
    paymentsDuck: paymentsDuck,
    interestRateDuck: interestRateDuck,
    saveDealXgFetcher: saveDealXgFetcher
  }, props)));
};

/*
 * InventoryListing is a layout component that handles a listing data object.
 * It organizes the data and components to be passed in to the Item Card component.
 *
 * LANDSCAPE:
 * ,----------------------------
 * |   Addons                   |
 * |............................|
 * |             |  Header      |
 * |   SideBar   |  Body        |
 * |             |..............|
 * |             |  Footer      |
 *  ----------------------------
 *
 * PORTRAIT:
 * ,-------------
 * |   Addons    |
 * |.............|
 * |   SideBar   |
 * |   Header    |
 * |   Body      |
 * |.............|
 * |   Footer    |
 *  -------------
 */
function InventoryListingV2(_ref5) {
  var _Checkbox;
  let {
    addOns = {},
    className,
    clickType,
    emailOwnerProps = {},
    enableMyWallet,
    fireTelmetricsTag = () => {},
    // deprecated
    handleEditEmail = () => {},
    isActiveDeal = false,
    isCompared = false,
    isHyundaiBuyOnlinePage = false,
    isPhoneNumberCallable = false,
    isRelevantSort = false,
    isSaved = false,
    lazyLoad = true,
    listing = {},
    listingCTA = {},
    listingQuickActions,
    loading,
    priceLabel,
    priceDescription,
    oneClickEmailSent = false,
    onActiveDealDetailsClick = () => {},
    onCertifiedTileClick,
    onCertifiedTileLinkClick,
    onCheckStatusClick = () => {},
    onCompareClick,
    onContextMenu = () => {},
    onDealerDiffCTAClick,
    onMyWalletClick,
    onOnlinePaperworkClick,
    onPaymentCTAClick,
    onPricingDetailClick = () => {},
    onProductTileClick,
    onProductTileLinkClick,
    onVhrBadgeClick,
    onSaveToggle,
    orientation,
    paginatedListingIndex,
    panelHeaderText = 'Recommended',
    parentId = '',
    paymentsCTA,
    personalPaymentProps,
    phoneClickHandler = () => {},
    ribbon = {},
    secondaryContent,
    shareButton,
    show360 = false,
    showDealerServices,
    showDistance = true,
    showEllipses = false,
    showEditEmail = false,
    showFooter = true,
    showListingPaymentRibbon,
    showPackages,
    showPaymentDetailsLink,
    showSections = {
      tiles: true
    },
    uiContext = 'default',
    updateListingPayments,
    onSelectPsxListing,
    dealsDuck,
    paymentsDuck,
    interestRateDuck,
    saveDealXgFetcher,
    ...rest
  } = _ref5;
  // deprecate fireTelmetricsTag
  phoneClickHandler = createChainedFunction(phoneClickHandler, fireTelmetricsTag);
  if (uiContext === 'snapshot' || uiContext === 'simplified') {
    orientation = 'portrait';
  }
  const rootClassName = clsx('inventory-listing', className, {
    'bg-brand-secondary': uiContext === 'alpha' || uiContext === 'recommended' || uiContext === 'sponsored' && !isRelevantSort,
    'panel-simplified margin-bottom-0': uiContext === 'simplified'
  });
  const Component = React.Fragment;
  let props;
  const renderAddOns = () => /*#__PURE__*/React.createElement(InventoryListingAddOns, Object.assign({
    listing: listing,
    orientation: orientation,
    key: "listingAddOns",
    shareButton: shareButton
  }, addOns));
  const renderOverlayActions = () => /*#__PURE__*/_jsx("div", {
    className: clsx('overlay-actions-container padding-top-2', {
      'col-xs-4': orientation === 'landscape',
      'col-xs-12': orientation === 'portrait',
      'padding-horizontal-0': uiContext !== 'simplified',
      'padding-horizontal-3': uiContext === 'simplified'
    })
  }, void 0, /*#__PURE__*/React.createElement(InventoryListingOverlayActions, Object.assign({
    orientation: orientation,
    onSaveToggle: onSaveToggle,
    listing: listing,
    isSaved: isSaved,
    show360: show360
  }, ['default', 'simplified', 'snapshot'].includes(uiContext) && {
    uiContext
  })));
  const getListingProps = () => ({
    orientation,
    sideBar: renderSideBar({
      lazyLoad,
      loading,
      listing,
      listingCTA,
      uiContext,
      orientation,
      onContextMenu
    }),
    header: null,
    body: renderBody({
      listing,
      listingCTA,
      priceLabel,
      priceDescription,
      onPricingDetailClick,
      paymentsCTA,
      uiContext,
      orientation,
      showPaymentDetailsLink,
      onCertifiedTileClick,
      onProductTileClick,
      onProductTileLinkClick,
      onVhrBadgeClick,
      onSaveToggle,
      isActiveDeal,
      isSaved,
      showPackages,
      showListingPaymentRibbon,
      enableMyWallet,
      onMyWalletClick,
      personalPaymentProps,
      updateListingPayments,
      onPaymentCTAClick,
      panelHeaderText,
      onContextMenu
    }),
    footer: renderFooter({
      clickType,
      emailOwnerProps,
      phoneClickHandler,
      handleEditEmail,
      isActiveDeal,
      isHyundaiBuyOnlinePage,
      isPhoneNumberCallable,
      listing,
      listingQuickActions,
      oneClickEmailSent,
      onActiveDealDetailsClick,
      onCheckStatusClick,
      onCertifiedTileClick,
      onCertifiedTileLinkClick,
      onDealerDiffCTAClick,
      onOnlinePaperworkClick,
      onProductTileClick,
      onProductTileLinkClick,
      parentId,
      uiContext,
      orientation,
      secondaryContent,
      showFooter,
      showEditEmail,
      showEllipses,
      showDistance,
      showDealerServices,
      paginatedListingIndex,
      onSelectPsxListing,
      dealsDuck,
      paymentsDuck,
      interestRateDuck,
      saveDealXgFetcher
    }),
    uiContext
  });
  const getListing = () => {
    const itemCardProps = getListingProps();

    // ItemCard does not have any specific support defined for `preorder` uiContext
    // deleting as it emits a console warning
    const isValidItemCardPropType = ['landscape', 'portrait', 'simplified', 'snapshot'].includes(itemCardProps.uiContext);
    if (!isValidItemCardPropType) {
      delete itemCardProps.uiContext;
    }
    const compareListingPanel = onCompareClick && /*#__PURE__*/_jsx(Panel.Heading, {
      className: "text-left",
      onClick: event => event.stopPropagation()
    }, void 0, _Checkbox || (_Checkbox = /*#__PURE__*/_jsx(Checkbox, {
      name: "compareCheckbox",
      checked: isCompared,
      label: "Compare",
      labelClassName: "margin-vertical-0",
      value: isCompared,
      onClick: onCompareClick
    })));
    return /*#__PURE__*/React.createElement(Component, props, compareListingPanel, renderAddOns(), renderOverlayActions(), /*#__PURE__*/React.createElement(ItemCard, itemCardProps));
  };

  // remove unused props before DOM node rest spread
  delete rest.dynamicSpecifications;
  delete rest.specificationKeys;
  delete rest.onTestDriveClose;
  delete rest.testDriveOwner;
  delete rest.secondaryContent;
  delete rest.secondaryFormHeader;
  delete rest.confirmationText;
  delete rest.birfParams;
  delete rest.confirmationText;
  delete rest.actions;
  return /*#__PURE__*/React.createElement(Panel, Object.assign({
    "data-cmp": "inventoryListing",
    className: rootClassName
  }, rest), /*#__PURE__*/_jsx(Panel.Body, {
    className: "padding-0 display-flex flex-grow-1 flex-column"
  }, void 0, getListing()));
}
export default InventoryListingV2;